import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent, type ConsentChoice } from "@/lib/consent";
import { disableAnalytics } from "@/lib/analytics";

/**
 * Opt-out cookie-consent banner (#486). Shows once until the visitor chooses.
 * Analytics run by default; declining persists the choice (via a cross-subdomain
 * cookie shared with marketing) and hard-stops the trackers for the rest of the
 * session.
 *
 * This banner only shows for direct-to-app visitors who haven't already made a
 * choice on the marketing site — the shared consent cookie means a visitor who
 * already accepted/declined on tristanoscatering.ca won't see this again.
 *
 * While it shows, its height is published as `--cookie-banner-height` on
 * `<html>` so fixed/sticky UI can stay clear of it (the /order cart, #1111, and
 * the public quote page's mobile CTA bar, #1126).
 */
export function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getConsent() === null) setShow(true);
  }, []);

  useEffect(() => {
    const el = bannerRef.current;
    if (!show || !el) return;
    const root = document.documentElement;
    // offsetHeight is the border box; contentRect would leave out the padding.
    const publish = () => root.style.setProperty("--cookie-banner-height", `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--cookie-banner-height");
    };
  }, [show]);

  if (!show) return null;

  const choose = (choice: ConsentChoice) => {
    setConsent(choice);
    if (choice === "declined") disableAnalytics();
    setShow(false);
  };

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background/95 px-4 py-4 shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-muted-foreground">
          We use cookies to measure site traffic and improve your experience. You can decline
          analytics anytime. See our{" "}
          <Link to="/privacy" className="underline hover:text-foreground">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" onClick={() => choose("declined")}>
            Decline
          </Button>
          <Button onClick={() => choose("accepted")}>Accept</Button>
        </div>
      </div>
    </div>
  );
}
